/**
 * Centralized Localization Utilities
 * Single source of truth for translation key/value detection and validation
 * 
 * Includes both syntactic and semantic validation based on element types.
 */

import { findElementTypeRule, ElementTypeRule, ElementStyle } from './localization/elementTypeRules';

// Generic words that indicate a placeholder or incomplete translation
const GENERIC_WORDS = [
  'Title', 'Description', 'Label', 'Button', 'Text', 'Message',
  'Error', 'Success', 'Placeholder', 'Hint', 'Name', 'Email',
  'Password', 'Submit', 'Cancel', 'Save', 'Delete', 'Edit',
  'Add', 'Remove', 'Close', 'Open', 'Back', 'Next', 'Previous',
  'Loading', 'Processing', 'Submitting...'
];

// Common TLDs to exclude from key-path detection
const DOMAIN_TLDS = /\.(com|org|net|io|co|de|fr|es|it|nl|uk|ca|au|br|mx|ar|cl|gov|edu|mil|int)$/i;

// Syntactic reason types (existing)
export type SyntacticReason = 
  | 'empty'
  | 'value_equals_key'
  | 'key_path_pattern'
  | 'only_interpolation'
  | 'generic_word'
  | 'too_short'
  | 'snake_case_only';

// Semantic reason types (new)
export type SemanticReason =
  | 'element_too_short'
  | 'element_too_long'
  | 'word_count_low'
  | 'word_count_high'
  | 'missing_period'
  | 'unexpected_period'
  | 'not_imperative'
  | 'placeholder_generic';

export type ProblematicReason = SyntacticReason | SemanticReason;

export interface SemanticContext {
  namespace: string;
  section: string;
  feature: string;
  elementType: string | null;
  rule: ElementTypeRule | null;
}

export interface ProblematicDetection {
  isProblematic: boolean;
  reasons: ProblematicReason[];
  context?: SemanticContext;
}

/**
 * Check if a value looks like a translation key path (but not a domain)
 * Key paths: common.actions.save, pages.home.hero.title
 * Not key paths: example.com, tax.network
 */
function isKeyPathPattern(value: string): boolean {
  // Must have 2+ dot-separated segments and contain underscore OR be lowercase only
  const hasKeyPathStructure = /^[a-z_][a-z0-9_]*(\.[a-z_][a-z0-9_]*)+$/.test(value);
  if (!hasKeyPathStructure) return false;
  
  // Exclude domain patterns
  if (DOMAIN_TLDS.test(value)) return false;
  
  // Key paths typically have underscores or 3+ segments
  const segments = value.split('.');
  return value.includes('_') || segments.length >= 3;
}

/**
 * Parse key to extract semantic context
 * Example: pages.dashboard.welcome.description
 *   → namespace: pages, section: dashboard, feature: welcome, elementType: description
 */
function parseKeyContext(key: string): SemanticContext {
  const parts = key.split('.');
  const lastPart = parts[parts.length - 1] || '';
  
  // Find matching element type rule
  const rule = findElementTypeRule(lastPart);
  
  return {
    namespace: parts[0] || 'unknown',
    section: parts[1] || 'unknown',
    feature: parts.length > 2 ? parts.slice(2, -1).join('.') : 'unknown',
    elementType: rule ? rule.suffixes[0] : null,
    rule
  };
}

/**
 * Count words in a string
 */
function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/**
 * Check if text starts with an imperative verb (simple heuristic)
 */
function isImperativeStyle(text: string): boolean {
  const trimmed = text.trim();
  // Should NOT start with publications or pronouns
  if (/^(The|A|An|This|That|These|Those|My|Your|Our|Their|Its|I|We|You|He|She|It|They)\s/i.test(trimmed)) {
    return false;
  }
  // Should NOT be a noun phrase starting with common nouns
  // (This is a simple heuristic - AI validation would be more accurate)
  return true;
}

/**
 * Check for generic placeholder patterns in placeholders
 */
function isGenericPlaceholder(value: string): boolean {
  const genericPatterns = [
    /^enter\s+(value|text|data|info|information)$/i,
    /^type\s+here$/i,
    /^input\s+(value|text)$/i,
    /^your\s+(value|text|input)$/i,
    /^placeholder$/i,
  ];
  return genericPatterns.some(pattern => pattern.test(value.trim()));
}

/**
 * Comprehensive problematic value detection
 * Used for "Needs Review" filtering across the platform
 * 
 * Syntactic checks:
 * - Empty/null values
 * - Value equals key (i18next fallback)
 * - Key-path patterns (but not domains)
 * - Only interpolation markers (no actual text)
 * - Generic placeholder words
 * - Too short values (≤2 chars)
 * - Snake_case only values
 * 
 * Semantic checks (based on element type):
 * - Character/word count vs element type expectations
 * - Period/punctuation requirements
 * - Writing style (imperative for buttons, etc.)
 * - Generic placeholder detection
 */
export function detectProblematicValue(key: string, value: string | null | undefined): ProblematicDetection {
  const reasons: ProblematicReason[] = [];
  const context = parseKeyContext(key);
  
  // === SYNTACTIC CHECKS ===
  
  // 1. Empty/null check
  if (value === null || value === undefined || value.trim() === '') {
    return { isProblematic: true, reasons: ['empty'], context };
  }
  
  const trimmedValue = value.trim();
  
  // 2. Value equals key (i18next fallback behavior)
  if (trimmedValue === key) {
    reasons.push('value_equals_key');
  }
  
  // 3. Looks like translation key path (but not domain)
  if (isKeyPathPattern(trimmedValue)) {
    reasons.push('key_path_pattern');
  }
  
  // 4. Only interpolation markers (no actual text content)
  if (/^\{\{.+\}\}$/.test(trimmedValue)) {
    reasons.push('only_interpolation');
  }
  
  // 5. Generic single word that indicates placeholder
  if (GENERIC_WORDS.includes(trimmedValue)) {
    reasons.push('generic_word');
  }
  
  // 6. Too short to be meaningful (≤2 chars, excluding common abbreviations)
  if (trimmedValue.length <= 2 && !/^(OK|No|Go|Hi|ID)$/i.test(trimmedValue)) {
    reasons.push('too_short');
  }
  
  // 7. Snake_case only (likely a key used as value)
  if (/^[a-z_]+$/.test(trimmedValue) && trimmedValue.includes('_') && trimmedValue.length < 30) {
    reasons.push('snake_case_only');
  }
  
  // === SEMANTIC CHECKS (based on element type) ===
  
  if (context.rule) {
    const rule = context.rule;
    const charCount = trimmedValue.length;
    const wordCount = countWords(trimmedValue);
    const endsWithPunctuation = /[.!?]$/.test(trimmedValue);
    
    // Character count checks
    if (charCount < rule.minChars) {
      reasons.push('element_too_short');
    }
    if (charCount > rule.maxChars) {
      reasons.push('element_too_long');
    }
    
    // Word count checks
    if (wordCount < rule.minWords) {
      reasons.push('word_count_low');
    }
    if (wordCount > rule.maxWords) {
      reasons.push('word_count_high');
    }
    
    // Punctuation checks
    if (rule.mustEndWithPeriod && !endsWithPunctuation) {
      reasons.push('missing_period');
    }
    if (rule.forbidPeriod && endsWithPunctuation) {
      reasons.push('unexpected_period');
    }
    
    // Style checks
    if (rule.style === 'imperative' && !isImperativeStyle(trimmedValue)) {
      reasons.push('not_imperative');
    }
    
    // Placeholder-specific checks
    if (context.elementType === 'placeholder' && isGenericPlaceholder(trimmedValue)) {
      reasons.push('placeholder_generic');
    }
  }
  
  return { 
    isProblematic: reasons.length > 0, 
    reasons,
    context
  };
}

/**
 * Simple boolean check for problematic values
 * Use this for quick filtering without needing reasons
 */
export function isProblematicValue(key: string, value: string | null | undefined): boolean {
  return detectProblematicValue(key, value).isProblematic;
}

/**
 * Convert localization key to auto-generated English value
 * Mirrors backend keyToEnglishValue() function
 */
export function keyToEnglishValue(key: string): string {
  const parts = key.split('.');
  const lastPart = parts[parts.length - 1];
  
  const suffixes = ['title', 'description', 'label', 'button', 'placeholder', 'error', 'success', 'hint', 'subtitle'];
  let meaningfulPart = lastPart;
  
  if (suffixes.includes(lastPart) && parts.length > 1) {
    meaningfulPart = parts.slice(-2).join('_');
  }
  
  return meaningfulPart
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

/**
 * Get human-readable description for problematic reason
 */
export function getProblematicReasonLabel(reason: ProblematicReason): string {
  const labels: Record<ProblematicReason, string> = {
    // Syntactic
    empty: 'Empty value',
    value_equals_key: 'Value equals key',
    key_path_pattern: 'Looks like key path',
    only_interpolation: 'Only interpolation markers',
    generic_word: 'Generic placeholder word',
    too_short: 'Too short',
    snake_case_only: 'Snake_case only',
    // Semantic
    element_too_short: 'Too short for element type',
    element_too_long: 'Too long for element type',
    word_count_low: 'Too few words for element type',
    word_count_high: 'Too many words for element type',
    missing_period: 'Missing ending punctuation',
    unexpected_period: 'Unexpected ending punctuation',
    not_imperative: 'Should use action verb',
    placeholder_generic: 'Generic placeholder text',
  };
  return labels[reason];
}

/**
 * Get severity level for a reason (for sorting/filtering)
 */
export function getReasonSeverity(reason: ProblematicReason): 'error' | 'warning' | 'info' {
  const errors: ProblematicReason[] = ['empty', 'value_equals_key', 'key_path_pattern', 'only_interpolation'];
  const warnings: ProblematicReason[] = ['generic_word', 'snake_case_only', 'element_too_short', 'missing_period', 'not_imperative'];
  
  if (errors.includes(reason)) return 'error';
  if (warnings.includes(reason)) return 'warning';
  return 'info';
}
